import { User } from '@/lib/types'
import { Model, ModelId } from '@/lib/types'
import { doesUserHavePositiveBalance, isUserFree } from '@/lib/user/utils'
import { models } from '@/lib/models/model-list'

/**
 * Model access rules:
 * - Guest users (no user object): Only have access to the free model (Gemini 2.0)
 * - Free users: Have access to basic and standard tier models
 * - Subscribed users (pro, advanced, basic): Have access to all models (basic, standard, premium)
 */

// The free model that any user (including guests) can access
export const FREE_MODEL_ID: ModelId[] = Object.values(models)
    .filter(model => 
        model.pricing.output_token && 
        model.pricing.output_token <= (0.4 / 1_000_000)
    )
    .map(model => model.id)

// Define the model tiers
export const MODEL_TIERS = {
    BASIC: 'basic',
    STANDARD: 'standard',
    PREMIUM: 'premium'
}

// Price thresholds for different pricing unit types ($ per unit)
export const PRICE_THRESHOLDS = {
    // Units: $ per million tokens (divide by 1000 for $ per 1K tokens)
    token: {
        premium: 20,    // Above this is premium
        standard: 1     // Above this is standard, below is basic
    }, 
    megapixel: {
        premium: 0.01, 
        standard: 0.003,
    }
    // Additional units can be added here as needed
}

/**
 * Get the tier of a model based on its pricing
 * @param model The model object
 * @returns The model tier (basic, standard, or premium)
 */
export function getModelTier(model: Model): string {    
    // Get pricing information
    const { 
        output_token, 
        output_image,
        output_image_mp
    } = model.pricing
    
    // Default to basic if we can't determine
    let tier = MODEL_TIERS.BASIC
    
    // Check output price against thresholds based on units
    if (model.pricing.output_token) {
        if (model.pricing.output_token * 1_000_000 >= PRICE_THRESHOLDS.token.premium) {
            tier = MODEL_TIERS.PREMIUM
        } else if (model.pricing.output_token * 1_000_000 >= PRICE_THRESHOLDS.token.standard) {
            tier = MODEL_TIERS.STANDARD
        }
    } else if (output_image_mp) {
        if (output_image_mp >= PRICE_THRESHOLDS.megapixel.premium) {
            tier = MODEL_TIERS.PREMIUM
        } else if (output_image_mp >= PRICE_THRESHOLDS.megapixel.standard) {
            tier = MODEL_TIERS.STANDARD
        }
    }

    // console.log('\x1b[36m%s\x1b[0m', `Model ${model.id} is ${tier} tier`)
    return tier
}

/**
 * Check if a user can use a specific model
 * @param user The user object or null for guest
 * @param model The model object
 * @returns Whether the user can access this model
 */
export function canUserAccessModel(user: User | null, model: Model): boolean {
    // Free model (Gemini) is always accessible for all users including guests
    if (FREE_MODEL_ID.includes(model.id)) return true
    
    // Guest users can only use the free model
    if (!user) return false
    
    // Subscribed users have access to all models
    if (doesUserHavePositiveBalance(user)) return true
    
    // Free users have access to basic and standard tier models
    if (isUserFree(user)) {
        const tier = getModelTier(model)
        return tier === MODEL_TIERS.BASIC || tier === MODEL_TIERS.STANDARD
    }
    
    return false
}

/**
 * Get all models a user can access
 * @param user The user object or null for guest
 * @param models Dictionary of models with ID keys and Model values
 * @returns Array of model IDs the user can access
 */
export function getAccessibleModels(user: User | null, models: Record<string, Model>): string[] {
    return Object.entries(models)
        .filter(([_, model]) => canUserAccessModel(user, model))
        .map(([id, _]) => id)
}

